import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';

export interface ProductionLogEntry {
  id: string;
  work_order_id: string;
  operator_id: string;
  pieces_completed: number;
  process_name: string;
  shift: string;
  created_at: string;
  work_order?: {
    order_number: string;
    product_name: string;
  };
  operator?: {
    full_name: string;
  };
}

export interface WorkOrderPause {
  id: string;
  work_order_id: string;
  operator_id: string;
  reason: string;
  started_at: string;
  ended_at: string | null;
  duration_minutes: number | null;
  work_order?: {
    order_number: string;
    product_name: string;
  };
  operator?: {
    full_name: string;
  };
}

export interface TraceabilityFilters {
  operatorId?: string;
  dateFrom?: string;
  dateTo?: string;
  workOrderId?: string;
  customerId?: string;
}

export function useOperatorHistory(filters: TraceabilityFilters) {
  // Production logs
  const productionLogs = useQuery({
    queryKey: ['production-logs', filters],
    queryFn: async () => {
      let query = api
        .from('operator_production_log')
        .select(`
          *,
          work_order:work_orders(order_number, product_name, customer_id),
          operator:profiles(full_name)
        `)
        .order('created_at', { ascending: false });

      if (filters.operatorId) {
        query = query.eq('operator_id', filters.operatorId);
      }
      if (filters.dateFrom) {
        query = query.gte('created_at', filters.dateFrom);
      }
      if (filters.dateTo) {
        query = query.lte('created_at', filters.dateTo + 'T23:59:59');
      }
      if (filters.workOrderId) {
        query = query.eq('work_order_id', filters.workOrderId);
      }

      const { data, error } = await query.limit(500);
      if (error) throw error;
      return data as ProductionLogEntry[];
    },
  });

  // Work order pauses
  const pauses = useQuery({
    queryKey: ['work-order-pauses', filters],
    queryFn: async () => {
      let query = api
        .from('work_order_pauses')
        .select(`
          *,
          work_order:work_orders(order_number, product_name),
          operator:profiles(full_name)
        `)
        .order('started_at', { ascending: false });

      if (filters.operatorId) {
        query = query.eq('operator_id', filters.operatorId);
      }
      if (filters.dateFrom) {
        query = query.gte('started_at', filters.dateFrom);
      }
      if (filters.dateTo) {
        query = query.lte('started_at', filters.dateTo + 'T23:59:59');
      }
      if (filters.workOrderId) {
        query = query.eq('work_order_id', filters.workOrderId);
      }

      const { data, error } = await query.limit(500);
      if (error) throw error;
      return data as WorkOrderPause[];
    },
  });

  // Summary stats
  const summary = useQuery({
    queryKey: ['production-summary', filters],
    queryFn: async () => {
      const logs = productionLogs.data || [];
      const pauseList = pauses.data || [];

      const totalPieces = logs.reduce((sum, l) => sum + (l.pieces_completed || 0), 0);
      const totalPauses = pauseList.length;
      const totalPauseMinutes = pauseList.reduce((sum, p) => sum + (p.duration_minutes || 0), 0);
      const uniqueOrders = new Set(logs.map(l => l.work_order_id)).size;
      const uniqueOperators = new Set(logs.map(l => l.operator_id)).size;

      return {
        totalPieces,
        totalPauses,
        totalPauseMinutes,
        uniqueOrders,
        uniqueOperators,
        avgPiecesPerLog: logs.length > 0 ? Math.round(totalPieces / logs.length) : 0,
      };
    },
    enabled: !!productionLogs.data && !!pauses.data,
  });

  return {
    productionLogs: productionLogs.data || [],
    pauses: pauses.data || [],
    summary: summary.data,
    isLoading: productionLogs.isLoading || pauses.isLoading,
    error: productionLogs.error || pauses.error,
    refetch: () => {
      productionLogs.refetch();
      pauses.refetch();
    },
  };
}
